import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  Box,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Typography,
  useTheme,
} from '@mui/material';
import {useCallback, useEffect, useRef, useState} from 'react';

interface Props {
  page: number;
  pageSize: number;
  totalItems: number;
  position?: {x: number; y: number};
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onPositionChange?: (position: {x: number; y: number}) => void;
}

const DraggablePagination = ({
  page,
  pageSize,
  totalItems,
  position,
  onPageChange,
  onPageSizeChange,
  onPositionChange,
}: Props) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({x: 0, y: 0});
  const [currentPosition, setCurrentPosition] = useState(
    position ?? {x: 80, y: 20}
  );
  const paperRef = useRef<HTMLDivElement>(null);

  const totalPages = Math.ceil(totalItems / pageSize);
  const startItem = Math.min(page * pageSize + 1, totalItems);
  const endItem = Math.min((page + 1) * pageSize, totalItems);

  const isDarkMode = theme.palette.mode === 'dark';

  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();

        setDragOffset({
          x: event.clientX - containerRect.left - currentPosition.x,
          y: event.clientY - containerRect.top - currentPosition.y,
        });
        setIsDragging(true);
      }
    },
    [currentPosition]
  );

  useEffect(() => {
    if (!isDragging) {
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();

        const newX = event.clientX - containerRect.left - dragOffset.x;
        const newY = event.clientY - containerRect.top - dragOffset.y;

        setCurrentPosition({x: newX, y: newY});
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      if (onPositionChange) {
        onPositionChange(currentPosition);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, currentPosition, onPositionChange]);

  const handlePrevPage = () => {
    if (page > 0) {
      onPageChange(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      onPageChange(page + 1);
    }
  };

  const handlePageSizeChange = (event: any) => {
    const newPageSize = event.target.value;
    onPageSizeChange(newPageSize);
    onPageChange(0);
  };

  if (totalItems === 0) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      sx={{position: 'relative', width: '100%', height: 0}}
    >
      <Paper
        ref={paperRef}
        sx={{
          position: 'absolute',
          top: currentPosition.y,
          left: currentPosition.x,
          zIndex: 1000,
          backgroundColor: isDarkMode
            ? 'rgba(30, 30, 30, 0.2)'
            : 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(8px)',
          padding: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: isDragging ? 'grabbing' : 'default',
          userSelect: 'none',
          boxShadow: 3,
          opacity: 0.85,
        }}
        elevation={3}
      >
        <Box
          sx={{
            cursor: 'grab',
            display: 'flex',
            alignItems: 'center',
            '&:active': {
              cursor: 'grabbing',
            },
          }}
          onMouseDown={handleMouseDown}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>

        <IconButton
          size="small"
          onClick={handlePrevPage}
          disabled={page <= 0}
          sx={{padding: 0.5}}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>

        <Typography variant="body2" sx={{minWidth: 80, textAlign: 'center'}}>
          {startItem}-{endItem} of {totalItems}
        </Typography>

        <IconButton
          size="small"
          onClick={handleNextPage}
          disabled={page >= totalPages - 1}
          sx={{padding: 0.5}}
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>

        <Select
          value={pageSize}
          onChange={handlePageSizeChange}
          size="small"
          sx={{
            minWidth: 70,
            '& .MuiSelect-select': {
              padding: '4px 8px',
              fontSize: '0.875rem',
            },
          }}
        >
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={20}>20</MenuItem>
          <MenuItem value={50}>50</MenuItem>
        </Select>
      </Paper>
    </Box>
  );
};

export default DraggablePagination;
